/**
 * Authentication Service
 * 
 * Handles all authentication-related operations including:
 * - Login/logout
 * - Password management
 * - Token refresh (triggered automatically by API client on 401)
 */

import { 
  getCachedLocalStorage, 
  setCachedLocalStorage, 
  removeCachedLocalStorage 
} from '../../../utils/storage';

const API_URL = process.env.REACT_APP_API_URL || '';


interface AuthResult {
  success: boolean;
  error?: string;
  challenge?: string;
  session?: string;
  message?: string;
}

interface ApiAuthResponse {
  success: boolean;
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
  error?: string;
  challenge?: string;
  session?: string;
  message?: string;
}

// ============================================
// Authentication
// ============================================

export const login = async (email: string, password: string): Promise<AuthResult> => {
  try {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    const data: ApiAuthResponse = await response.json();

    if (data.success) {
      setCachedLocalStorage('quazi_access_token', data.access_token!);
      setCachedLocalStorage('quazi_id_token', data.id_token!);
      setCachedLocalStorage('quazi_refresh_token', data.refresh_token!);
      setCachedLocalStorage('quazi_user', email);
      return { success: true };
    } else if (data.challenge === 'NEW_PASSWORD_REQUIRED') {
      return {
        success: false,
        challenge: 'NEW_PASSWORD_REQUIRED',
        session: data.session,
        message: data.message,
      };
    } else {
      return { success: false, error: data.error || 'Login failed' };
    }
  } catch (error) {
    return { success: false, error: 'Network error. Please try again.' };
  }
};

export const setNewPassword = async (email: string, newPassword: string, session: string): Promise<AuthResult> => {
  try {
    const response = await fetch(`${API_URL}/api/auth/new-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, new_password: newPassword, session }),
    });

    const data: ApiAuthResponse = await response.json();

    if (data.success) {
      setCachedLocalStorage('quazi_access_token', data.access_token!);
      setCachedLocalStorage('quazi_id_token', data.id_token!);
      setCachedLocalStorage('quazi_refresh_token', data.refresh_token!);
      setCachedLocalStorage('quazi_user', email);
      return { success: true };
    } else {
      return { success: false, error: data.error || 'Failed to set new password' };
    }
  } catch (error) {
    return { success: false, error: 'Network error. Please try again.' };
  }
};

// ============================================
// Password Reset
// ============================================

export const forgotPassword = async (email: string): Promise<AuthResult> => {
  try {
    const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    return { success: false, error: 'Network error. Please try again.' };
  }
};

export const confirmForgotPassword = async (email: string, confirmationCode: string, newPassword: string): Promise<AuthResult> => {
  try {
    const response = await fetch(`${API_URL}/api/auth/confirm-forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        confirmation_code: confirmationCode,
        new_password: newPassword,
      }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    return { success: false, error: 'Network error. Please try again.' };
  }
};

// ============================================
// Token Management
// ============================================

export const refreshToken = async (): Promise<{ success: boolean }> => {
  try {
    const refreshTokenValue = getCachedLocalStorage('quazi_refresh_token');
    const email = getCachedLocalStorage('quazi_user');

    if (!refreshTokenValue || !email) {
      return { success: false };
    }

    const response = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshTokenValue, email }),
    });

    if (!response.ok) {
      return { success: false };
    }

    const data: ApiAuthResponse = await response.json();

    if (data.success) {
      setCachedLocalStorage('quazi_access_token', data.access_token!);
      setCachedLocalStorage('quazi_id_token', data.id_token!);
      return { success: true };
    } else {
      return { success: false };
    }
  } catch (error) {
    return { success: false };
  }
};

export const logout = async (): Promise<void> => {
  try {
    const accessToken = getCachedLocalStorage('quazi_access_token');

    if (accessToken) {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ access_token: accessToken }),
      });
    }
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    clearAuthStorage();
  }
};

// ============================================
// Auth State Helpers
// ============================================

const clearAuthStorage = (): void => {
  removeCachedLocalStorage('quazi_access_token');
  removeCachedLocalStorage('quazi_id_token');
  removeCachedLocalStorage('quazi_refresh_token');
  removeCachedLocalStorage('quazi_user');
};

export const isAuthenticated = (): boolean => {
  return !!getCachedLocalStorage('quazi_access_token');
};

export const getUser = (): string | null => {
  return getCachedLocalStorage('quazi_user');
};

export const getAccessToken = (): string | null => {
  return getCachedLocalStorage('quazi_access_token');
};
